import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { FeaturedButton, FeaturedBadge, FeaturedStats, useFeaturedReturn } from "../components/Featured";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Wrench, Plus, Loader2, CreditCard, Calendar, CheckCircle, AlertCircle, MapPin, Edit, Trash2, Eye, Phone, MessageSquare, Mail, MessageCircle, Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICINO_LOCATIONS = [
  "Airolo", "Arbedo-Castione", "Arogno", "Arzo", "Ascona", "Astano", "Balerna", "Bedano", 
  "Bedigliora", "Bellinzona", "Biasca", "Bioggio", "Bissone", "Blenio", "Bodio", "Bosco Gurin",
  "Breganzona", "Brione sopra Minusio", "Brissago", "Brusino Arsizio", "Cademario", "Cadempino", 
  "Cadenazzo", "Canobbio", "Capriasca", "Caslano", "Castel San Pietro", "Centovalli", "Chiasso", 
  "Claro", "Coldrerio", "Collina d'Oro", "Comano", "Croglio", "Cugnasco-Gerra", "Cureglia", "Curio",
  "Faido", "Gambarogno", "Giornico", "Gordola", "Grancia", "Gravesano", "Lamone", "Lavizzara",
  "Ligornetto", "Locarno", "Losone", "Lugano", "Lumino", "Maggia", "Manno", "Maroggia", 
  "Massagno", "Melano", "Melide", "Mendrisio", "Meride", "Mezzovico-Vira", "Minusio", "Monteceneri",
  "Monteggio", "Morbio Inferiore", "Morcote", "Muralto", "Neggio", "Novaggio", "Novazzano", 
  "Origlio", "Orselina", "Paradiso", "Personico", "Pollegio", "Ponte Capriasca", "Ponte Tresa",
  "Porza", "Prato Leventina", "Pregassona", "Pura", "Quinto", "Rivera", "Ronco sopra Ascona",
  "Rovio", "San Nazzaro", "Sant'Antonino", "Savosa", "Sementina", "Serravalle", "Sorengo", 
  "Stabio", "Tenero-Contra", "Terre di Pedemonte", "Torricella-Taverne", "Tremona", "Vacallo", 
  "Vernate", "Vezia", "Vico Morcote", "Viganello", "Remote"
];

// Metodi di contatto disponibili
const CONTACT_METHODS = [
  { value: "internal", label: "Messaggistica interna", icon: MessageSquare, description: "Solo attraverso il sistema di messaggistica dell'app" },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle, description: "Contatto tramite WhatsApp" },
  { value: "email", label: "Email", icon: Mail, description: "Mostra l'indirizzo email nell'annuncio" },
  { value: "phone", label: "Chiamata", icon: Phone, description: "Mostra il numero di telefono nell'annuncio" },
];

export default function ProviderDashboard() {
  const { user, refreshUser } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteServiceId, setDeleteServiceId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [serviceForm, setServiceForm] = useState({
    title: "", description: "", tariffa: "", image_url: "", contact_methods: ["internal"], location: ""
  });

  useFeaturedReturn(() => fetchData());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await refreshUser();
      if (user?.subscription_active) {
        const servicesRes = await axios.get(`${API}/provider/services`);
        setServices(servicesRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openNewServiceModal = () => {
    setEditingService(null);
    setServiceForm({ title: "", description: "", tariffa: "", image_url: "", contact_methods: ["internal"], location: user?.comune_residenza || "" });
    setShowServiceModal(true);
  };

  const openEditServiceModal = (service) => {
    setEditingService(service);
    // Handle both old contact_method (single) and new contact_methods (array)
    const contactMethods = service.contact_methods || (service.contact_method ? [service.contact_method] : ["internal"]);
    setServiceForm({
      title: service.title,
      description: service.description,
      tariffa: service.tariffa || "",
      image_url: service.image_url || "",
      contact_methods: contactMethods,
      location: service.location || ""
    });
    setShowServiceModal(true);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Seleziona un file immagine valido");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'immagine non può superare 5MB");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/provider/services/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setServiceForm(prev => ({ ...prev, image_url: res.data.url }));
      toast.success("Immagine caricata!");
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Errore nel caricamento dell'immagine");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveService = async () => {
    if (!serviceForm.title || !serviceForm.description) {
      toast.error("Titolo e descrizione sono obbligatori");
      return;
    }
    
    if (!serviceForm.contact_methods || serviceForm.contact_methods.length === 0) {
      toast.error("Seleziona almeno un metodo di contatto");
      return;
    }

    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, serviceForm);
        toast.success("Servizio aggiornato!");
      } else {
        await axios.post(`${API}/services`, serviceForm);
        toast.success("Servizio pubblicato!");
      }
      setShowServiceModal(false);
      setServiceForm({ title: "", description: "", tariffa: "", image_url: "", contact_methods: ["internal"], location: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async () => {
    if (!deleteServiceId) return;
    try {
      await axios.delete(`${API}/services/${deleteServiceId}`);
      toast.success("Servizio eliminato");
      setDeleteServiceId(null);
      fetchData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("it-CH", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="provider-dashboard">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{user?.ragione_sociale || user?.name}</h1>
            <p className="text-slate-600 mt-1">Gestisci i tuoi servizi</p>
          </div>
          {user?.subscription_active && (
            <Button onClick={openNewServiceModal} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Nuovo servizio
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" /> Abbonamento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`flex items-center gap-2 mb-4 ${user?.subscription_active ? "text-emerald-600" : "text-red-600"}`}>
                  {user?.subscription_active ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                  <span className="font-semibold">{user?.subscription_active ? "Attivo" : "Non attivo"}</span>
                </div>
                
                {user?.subscription_active ? (
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Scade il {user.subscription_expires ? formatDate(user.subscription_expires) : "N/A"}
                  </p>
                ) : (
                  <div>
                    <div className="bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg inline-block mb-2">
                      ABBONAMENTO ANNUALE
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 mb-2">
                      <p className="text-emerald-800 font-bold text-sm">Validità 365 giorni</p>
                    </div>
                    <p className="text-sm text-slate-500 mb-1">
                      <span className="font-semibold">48 CHF</span> / anno
                    </p>
                    <p className="text-xs text-amber-600 mb-3">Usa codice LANCIO50 per -50%</p>
                    <Link to="/subscription">
                      <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">Attiva abbonamento</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Statistiche</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-emerald-600">{services.length}</div>
                  <div className="text-sm text-slate-500">Servizi pubblicati</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Services */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-emerald-600" /> I tuoi servizi
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><div className="spinner"></div></div>
                ) : !user?.subscription_active ? (
                  <div className="text-center py-8 max-w-sm mx-auto">
                    <div className="bg-emerald-700 text-white text-base font-bold px-4 py-2 rounded-xl inline-block mb-4">
                      ABBONAMENTO ANNUALE
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-3">Pubblica i tuoi servizi</h3>
                    
                    {/* Duration Highlight */}
                    <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 mb-3">
                      <p className="text-emerald-800 font-bold text-lg">Validità 365 giorni</p>
                      <p className="text-emerald-600 text-sm">Meno di 4 CHF al mese</p>
                    </div>
                    
                    {/* Price Secondary */}
                    <p className="text-slate-500 mb-2">
                      <span className="text-xl font-semibold">48 CHF</span>
                      <span className="text-sm"> / anno</span>
                    </p>
                    
                    <p className="text-sm text-amber-600 font-medium bg-amber-50 py-2 px-3 rounded-lg inline-block mb-4">
                      Usa codice LANCIO50 per -50%
                    </p>
                    
                    <Link to="/subscription"><Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">Attiva ora</Button></Link>
                  </div>
                ) : services.length > 0 ? (
                  <div className="space-y-4">
                    {services.map((service) => (
                      <div key={service.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex flex-col sm:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Eye className="w-3 h-3" /> {service.views || 0}
                              </span>
                              {service.tariffa && <Badge variant="secondary">{service.tariffa}</Badge>}
                            </div>
                            <h3 className="font-semibold text-slate-900">{service.title}</h3>
                            <FeaturedBadge item={service} className="mt-1" />
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{service.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{service.location}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <FeaturedButton kind="service" item={service} />
                            <Button variant="outline" size="sm" onClick={() => openEditServiceModal(service)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteServiceId(service.id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <FeaturedStats item={service} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun servizio</h3>
                    <Button onClick={openNewServiceModal} variant="outline"><Plus className="w-4 h-4 mr-2" /> Crea</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Service Modal */}
      <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingService ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div>
              <Label>Titolo servizio *</Label>
              <Input value={serviceForm.title} onChange={(e) => setServiceForm({...serviceForm, title: e.target.value})} placeholder="es. Riparazioni idrauliche" className="mt-1.5" />
            </div>
            <div>
              <Label>Descrizione *</Label>
              <Textarea value={serviceForm.description} onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})} placeholder="Descrivi il servizio che offri..." rows={4} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tariffa indicativa</Label>
                <Input value={serviceForm.tariffa} onChange={(e) => setServiceForm({...serviceForm, tariffa: e.target.value})} placeholder="es. CHF 50/ora" className="mt-1.5" />
              </div>
              <div>
              <Label>Località *</Label>
              <Select value={serviceForm.location} onValueChange={(v) => setServiceForm({...serviceForm, location: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Seleziona località" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {TICINO_LOCATIONS.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            </div>
            
            {/* Upload Immagine */}
            <div>
              <Label>Immagine Servizio (opzionale)</Label>
              <p className="text-xs text-slate-500 mb-2">Carica un'immagine rappresentativa del tuo servizio (max 5MB)</p>
              
              {serviceForm.image_url ? (
                <div className="relative mt-2">
                  <img 
                    src={serviceForm.image_url.startsWith('http') 
                      ? serviceForm.image_url 
                      : `${process.env.REACT_APP_BACKEND_URL}${serviceForm.image_url}`
                    } 
                    alt="Preview" 
                    className="w-full h-40 object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={() => setServiceForm({...serviceForm, image_url: ""})}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {uploadingImage ? (
                      <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                        <p className="text-sm text-slate-500">Clicca per caricare</p>
                        <p className="text-xs text-slate-400">JPG, PNG, WebP (max 5MB)</p>
                      </>
                    )}
                  </div>
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                </label>
              )}
            </div>
            
            {/* Metodi di Contatto - 4 Checkbox */}
            <div>
              <Label>Metodo di contatto preferito *</Label>
              <p className="text-xs text-slate-500 mb-2">Seleziona almeno un metodo</p>
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200 space-y-3">
                {CONTACT_METHODS.map((method) => {
                  const Icon = method.icon;
                  return (
                    <div key={method.value} className="flex items-start space-x-3">
                      <Checkbox 
                        id={`provider-contact-${method.value}`}
                        checked={serviceForm.contact_methods.includes(method.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setServiceForm({...serviceForm, contact_methods: [...serviceForm.contact_methods, method.value]});
                          } else {
                            setServiceForm({...serviceForm, contact_methods: serviceForm.contact_methods.filter(m => m !== method.value)});
                          }
                        }}
                      />
                      <div className="flex-1">
                        <label htmlFor={`provider-contact-${method.value}`} className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {method.label}
                        </label>
                        <p className="text-xs text-slate-500">{method.description}</p>
                      </div>
                    </div>
                  );
                })}
                {serviceForm.contact_methods.includes("phone") && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Il telefono associato al tuo account verrà mostrato
                  </p>
                )}
                {serviceForm.contact_methods.includes("email") && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> L'email associata al tuo account verrà mostrata
                  </p>
                )}
                {serviceForm.contact_methods.includes("whatsapp") && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Il numero WhatsApp associato al tuo account verrà mostrato
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceModal(false)}>Annulla</Button>
            <Button onClick={handleSaveService} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</> : editingService ? "Salva" : "Pubblica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteServiceId} onOpenChange={() => setDeleteServiceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il servizio?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteService} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
